import type { RoomMusicState } from './RoomMusic.js';
import { musicAt } from './RoomMusicTimeline.js';

/** Audience metadata only. Never include media URLs, transport anchors or a queue. */
export interface MusicNowPlaying {
  revision: number;
  /** Time of the persisted state change, distinct from this metadata observation. */
  changedAtMs: number;
  sampledAtMs: number;
  status: 'playing' | 'paused' | 'off';
  track: { title: string; artist: string; playlist: string | null } | null;
}

const label = (value: string) => value.replace(/[\p{Cc}\p{Cf}]/gu, ' ').trim().slice(0, 240);

/** Resolve labels at the current room instant, never at a pending command's deadline. */
export function projectMusicNowPlaying(state: RoomMusicState | null, sampledAtMs: number): MusicNowPlaying {
  const off: MusicNowPlaying = { revision: state?.revision ?? 0, changedAtMs: state?.serverTimeMs ?? sampledAtMs,
    sampledAtMs, status: 'off', track: null };
  if (!state?.includeInProgram) return off;
  const current = musicAt(state, sampledAtMs);
  if (!current.track || current.transport.status === 'off'
    || (current.transport.status === 'playing' && current.transport.virtualStartAtMs! > sampledAtMs)) return off;
  // Keep the outgoing title visible through the normal 250 ms inter-track gap.
  return {
    revision: state.revision, changedAtMs: state.serverTimeMs, sampledAtMs, status: current.transport.status,
    track: {
      title: label(current.track.title), artist: label(current.track.artist),
      playlist: current.playlist ? label(current.playlist.title) : null,
    },
  };
}

/** Validate and copy an allowlist at the public boundary, dropping unexpected fields. */
export function parseMusicNowPlaying(value: unknown): MusicNowPlaying | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as MusicNowPlaying;
  if (!Number.isSafeInteger(candidate.revision) || candidate.revision < 0
    || !Number.isSafeInteger(candidate.changedAtMs) || candidate.changedAtMs < 0
    || !Number.isSafeInteger(candidate.sampledAtMs) || candidate.sampledAtMs < 0
    || !['off', 'playing', 'paused'].includes(candidate.status)) return null;
  const { revision, changedAtMs, sampledAtMs, status } = candidate;
  if (status === 'off') return { revision, changedAtMs, sampledAtMs, status, track: null };
  const track = candidate.track;
  if (!track || typeof track.title !== 'string' || typeof track.artist !== 'string'
    || (track.playlist !== null && typeof track.playlist !== 'string')) return null;
  return { revision, changedAtMs, sampledAtMs, status,
    track: { title: label(track.title), artist: label(track.artist), playlist: track.playlist === null ? null : label(track.playlist) } };
}

/** A slow worker's old revision must not win merely because it published later.
 * The change timestamp also permits revisions to restart after a room is cleared. */
export function musicNowPlayingIsOlder(next: MusicNowPlaying, previous: MusicNowPlaying): boolean {
  if (next.changedAtMs !== previous.changedAtMs) return next.changedAtMs < previous.changedAtMs;
  if (next.revision !== previous.revision) return next.revision < previous.revision;
  return next.sampledAtMs < previous.sampledAtMs;
}
